import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { UserCard } from "@/components/user-card/UserCard.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import {
  useEmployeeDetails,
  useEmployeeMe,
  useEmployees,
} from "@/queries/employees/use-employees.query.ts";
import { getActiveEmployeeLifecycleStatuses } from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  Avatar,
  Box,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export const HomeMyTeam = () => {
  const { t } = useTranslation();
  const { data: employee } = useEmployeeMe();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const { canIEdit, canCurrentEmployeeEditField } = usePermissions();

  const canViewEmployees = canIEdit(ApiPermission.see_employee_directory);

  const { data: employeeFull, isLoading: isEmployeeFullLoading } = useEmployeeDetails(
    employee?.id || undefined
  );
  const employeeFullPosition = employeeFull?.employee_position;
  const employeeFullDepartment = employeeFullPosition?.department;
  const employeeFullManager = employeeFullPosition?.manager;

  const { data: myTeamEmployees, isLoading: isMyTeamEmployees } = useEmployees(
    {
      page_number: 1,
      manager_ids: employee ? [employee.id] : [],
      employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
    },
    !!employee && canViewEmployees
  );

  if (!employee) return null;

  return (
    <>
      <Title
        header={t("common:my_team")}
        size={"lg"}
        paddingBottom
        paddingTop
        actionIcon={<FontAwesomeIcon icon={"fa-solid fa-chevron-right"} />}
        to={"/employees"}
      />

      {isEmployeeFullLoading && isMyTeamEmployees && <Skeleton className={"w-full h-64"} />}
      <Stack direction={"horizontal"} gap={"md"}>
        {employeeFullDepartment && (
          <UserCard
            size={"md"}
            title={employeeFullDepartment.title}
            caption={t("core:department")}
            to={
              canCurrentEmployeeEditField(EmployeeFieldPermission.see_departments_structure)
                ? `/departments/chart?department_id=${employeeFullDepartment.id}`
                : ""
            }
            avatarElement={
              <Avatar
                emoji={employeeFullDepartment.emoji || undefined}
                url={employeeFullDepartment.image_url || undefined}
                acronym={employeeFullDepartment.title}
                size={"2xl"}
              />
            }
          />
        )}
        {myTeamEmployees && myTeamEmployees.items.length > 0 && (
          <UserCard
            to={
              canCurrentEmployeeEditField(EmployeeFieldPermission.see_organization_structure)
                ? `/employees/chart?employee_id=${employee.id}`
                : ""
            }
            size={"md"}
            title={t("core:my_subordinates")}
            caption={t("plurals:employees_count", {
              count: myTeamEmployees.metadata.total_items,
            })}
            avatarElement={
              <Box
                className={
                  "p-(--gap-smd) bg-brand-dark/5 rounded-sm aspect-square grid grid-cols-2 gap-sm"
                }
              >
                {myTeamEmployees.items.slice(0, 4).map((directEmployee) => (
                  <EmployeeAvatar
                    key={`direct-employee-${directEmployee.id}`}
                    employee={directEmployee}
                    size={"xl"}
                  />
                ))}
              </Box>
            }
          />
        )}
        {employeeFullManager && (
          <UserCard
            className={"self-start"}
            onClick={() => {
              openModal(EmployeeViewModal, {
                employeeId: employeeFullManager.id,
              });
            }}
            size={"md"}
            to={`/employees/${employeeFullManager.id}`}
            title={renderEmployeeName(employeeFullManager)}
            caption={t("core:my_manager")}
            avatarElement={<EmployeeAvatar employee={employeeFullManager} size={"2xl"} />}
          />
        )}
      </Stack>
    </>
  );
};
